import { formatMessage, loadMessages } from 'devextreme/localization';
import type {
    Column,
    ColumnCellTemplateData,
    Properties as DataGridOptions,
} from 'devextreme/ui/data_grid';

loadMessages({ vi: { 'vlp-grid-row-number': 'STT' }, en: { 'vlp-grid-row-number': 'No.' }, de: { 'vlp-grid-row-number': 'Nr.' } });

export const VLP_GRID_ROW_NUMBER_COLUMN = '__vlpRowNumber';
export const VLP_GRID_ROW_NUMBER_OPT_OUT_ATTRIBUTE = 'data-vlp-row-number';

type GridComponent = {
    addColumn: (column: Column | string) => void;
    element?: () => unknown;
    getVisibleColumns: () => Column[];
    columnOption?: (id: string, option: string, value: unknown) => void;
    pageIndex?: () => number;
    pageSize?: () => number;
};

type GridInitializedEvent = {
    component?: GridComponent;
    element?: unknown;
};

type GridVisibleRow = {
    rowType?: string;
    /** Index among the DATA rows of the current page (DevExtreme skips detail/group rows here). */
    dataIndex?: number;
};

type GridCellInfo = {
    component?: {
        element?: () => unknown;
        pageIndex?: () => number;
        pageSize?: () => number;
        getVisibleRows?: () => GridVisibleRow[];
    };
    rowIndex?: number;
    rowType?: string;
};

const ROW_NUMBER_CAPTIONS = new Set(['#', 'no', 'no.', 'nr', 'nr.', 'no-col', 'stt', 'số tt', 'số thứ tự']);
const ROW_NUMBER_TEMPLATE_PATTERN = /(row(?:number|index)|serial(?:number)?|(?:custom)?index|[a-z]*number|stt|notemplate)/i;
const COMPACT_FIELD_PATTERN = /(^|\.)(active|archive|enabled|locked|default|selected|status|state|type|level|priority|currency|country)$/i;
const CODE_FIELD_PATTERN = /(code|number|prefix|tracking|reference|externalid|currencycode|countrycode)$/i;
const DATE_FIELD_PATTERN = /(date|time|at|from|to|effective|expired|expiry|created|updated)$/i;
const MONEY_FIELD_PATTERN = /(amount|balance|price|cost|revenue|profit|fee|tax|total|limit|debit|credit|rate|weight|volume|quantity|pieces)$/i;
const NAME_FIELD_PATTERN = /(name|title|email|phone|service|vendor|customer|account|tenant|organization|facility|warehouse)$/i;
const LONG_TEXT_FIELD_PATTERN = /(address|description|content|message|note|reason|path|scope|condition|expression|payload)$/i;
const gridsWithRowNumber = new WeakSet<object>();

function unwrapElement(element: unknown): HTMLElement | undefined {
    if (!element) return undefined;
    if (element instanceof HTMLElement) return element;
    return typeof (element as { get?: unknown }).get === 'function'
        ? (element as { get: (index: number) => HTMLElement | undefined }).get(0)
        : undefined;
}

function normalizedCaption(column: Column): string {
    return String(column?.caption ?? '').trim().toLocaleLowerCase();
}

function templateName(column: Column): string {
    return typeof column?.cellTemplate === 'string' ? column.cellTemplate : '';
}

export function isVlpGridRowNumberColumn(column: Column): boolean {
    if (!column) return false;
    if (column.name === VLP_GRID_ROW_NUMBER_COLUMN) return true;
    if (String(column.cssClass ?? '').split(/\s+/).includes('vlp-grid-row-number')) return true;
    if (ROW_NUMBER_CAPTIONS.has(normalizedCaption(column))) return true;

    const template = templateName(column);
    const width = Number(column.width ?? column.minWidth ?? 0);
    return !column.dataField
        && !!template
        && ROW_NUMBER_TEMPLATE_PATTERN.test(template)
        && (width === 0 || width <= 100);
}

/**
 * Chỉ số của dòng trong SỐ DÒNG DỮ LIỆU của trang, không phải chỉ số hiển thị.
 *
 * `cell.rowIndex` của DevExtreme là chỉ số HIỂN THỊ nên mọi dòng phụ (master-detail đang bung, dòng
 * nhóm) cũng chiếm một chỉ số: mở một dòng chi tiết là toàn bộ STT phía dưới bị cộng thêm 1. Ưu tiên
 * `dataIndex` của chính dòng đó; không có thì đếm số dòng dữ liệu đứng trước.
 */
function dataRowIndexOf(cell: GridCellInfo, visibleIndex: number): number {
    const rows = cell.component?.getVisibleRows?.() ?? [];
    const dataIndex = Number(rows[visibleIndex]?.dataIndex);
    if (Number.isFinite(dataIndex) && dataIndex >= 0) return dataIndex;
    if (rows.length > visibleIndex) {
        return rows.slice(0, visibleIndex).filter((row) => row?.rowType === 'data').length;
    }
    return visibleIndex;
}

export function calculateVlpGridRowNumber(cell: GridCellInfo): number | null {
    if (cell?.rowType && cell.rowType !== 'data') return null;

    const visibleIndex = Number(cell?.rowIndex);
    if (!Number.isFinite(visibleIndex) || visibleIndex < 0) return null;
    const rowIndex = dataRowIndexOf(cell, visibleIndex);

    const pageIndex = Math.max(0, Number(cell.component?.pageIndex?.() ?? 0) || 0);
    const pageSize = Math.max(0, Number(cell.component?.pageSize?.() ?? 0) || 0);
    const rootElement = unwrapElement(cell.component?.element?.());
    const externalOffset = Math.max(
        0,
        Number(rootElement?.getAttribute('data-vlp-row-offset') ?? 0) || 0,
    );
    return externalOffset + pageIndex * pageSize + rowIndex + 1;
}

export function createVlpGridRowNumberColumn(): Column {
    return {
        name: VLP_GRID_ROW_NUMBER_COLUMN,
        caption: formatMessage('vlp-grid-row-number'),
        width: 60,
        minWidth: 60,
        alignment: 'center',
        fixed: true,
        fixedPosition: 'left',
        allowEditing: false,
        allowExporting: false,
        allowFiltering: false,
        allowFixing: false,
        allowGrouping: false,
        allowHeaderFiltering: false,
        allowReordering: false,
        allowResizing: false,
        allowSearch: false,
        allowSorting: false,
        showInColumnChooser: false,
        visibleIndex: 0,
        cssClass: 'vlp-grid-row-number',
        cellTemplate: (container: HTMLElement, cell: ColumnCellTemplateData) => {
            const rowNumber = calculateVlpGridRowNumber(cell);
            container.textContent = rowNumber === null ? '' : String(rowNumber);
        },
    };
}

export function ensureVlpGridRowNumber(event: GridInitializedEvent): void {
    const component = event?.component;
    const rootElement = unwrapElement(event?.element);
    if (!component?.addColumn || !component.getVisibleColumns) return;
    if (rootElement?.getAttribute(VLP_GRID_ROW_NUMBER_OPT_OUT_ATTRIBUTE) === 'off') return;
    const columns = component.getVisibleColumns() ?? [];
    const managedColumn = columns.find(column => column.name === VLP_GRID_ROW_NUMBER_COLUMN);
    const caption = formatMessage('vlp-grid-row-number');
    if (managedColumn && managedColumn.caption !== caption) {
        // Update the existing column on locale repaint; never add a duplicate.
        component.columnOption?.(VLP_GRID_ROW_NUMBER_COLUMN, 'caption', caption);
    }
    if (gridsWithRowNumber.has(component)) return;
    if (columns.length === 0) return;
    if (columns.some(isVlpGridRowNumberColumn)) {
        gridsWithRowNumber.add(component);
        return;
    }

    // `onInitialized` and `onContentReady` can run before DevExtreme exposes the
    // column added by the previous callback. Guard the grid instance itself so a
    // fast second callback cannot add another column with the same name (E1059).
    gridsWithRowNumber.add(component);
    try {
        component.addColumn(createVlpGridRowNumberColumn());
    } catch (error) {
        gridsWithRowNumber.delete(component);
        throw error;
    }
}

function inferredColumnMinWidth(column: Column): number {
    if (column.type === 'buttons') {
        const buttonCount = Array.isArray(column.buttons) ? column.buttons.length : 1;
        return Math.max(72, Math.min(176, 48 + buttonCount * 36));
    }

    const field = String(column.dataField ?? column.name ?? '').replace(/[^a-z0-9.]/gi, '');
    const captionLength = String(column.caption ?? '').trim().length;
    const captionWidth = captionLength > 0
        ? Math.min(240, Math.max(96, captionLength * 7 + 36))
        : 0;

    if (column.dataType === 'boolean') return Math.max(96, captionWidth);
    if (column.dataType === 'date') return Math.max(140, captionWidth);
    if (column.dataType === 'datetime') return Math.max(170, captionWidth);
    if (LONG_TEXT_FIELD_PATTERN.test(field)) return Math.max(260, captionWidth);
    if (NAME_FIELD_PATTERN.test(field)) return Math.max(190, captionWidth);
    if (DATE_FIELD_PATTERN.test(field)) return Math.max(150, captionWidth);
    if (MONEY_FIELD_PATTERN.test(field) || column.dataType === 'number') return Math.max(128, captionWidth);
    if (CODE_FIELD_PATTERN.test(field)) return Math.max(136, captionWidth);
    if (COMPACT_FIELD_PATTERN.test(field)) return Math.max(112, captionWidth);
    return Math.max(120, captionWidth);
}

export function configureVlpGridColumns(columns: Column[]): void {
    for (const column of columns ?? []) {
        if (!column || isVlpGridRowNumberColumn(column)) continue;
        if (column.width !== undefined || column.minWidth !== undefined) continue;
        column.minWidth = inferredColumnMinWidth(column);
    }
}

export function installVlpGridHeaderTooltips(documentRef: Document): () => void {
    const handlePointerOver = (event: Event): void => {
        const target = event.target as HTMLElement | null;
        const textElement = target?.closest?.('.dx-datagrid-headers .dx-datagrid-text-content') as HTMLElement | null;
        if (!textElement) return;

        const caption = textElement.textContent?.trim();
        if (caption) textElement.title = caption;
    };

    documentRef.addEventListener('mouseover', handlePointerOver);
    return () => documentRef.removeEventListener('mouseover', handlePointerOver);
}

export const vlpDataGridDefaultOptions: DataGridOptions = {
    columnAutoWidth: true,
    allowColumnResizing: true,
    columnResizingMode: 'widget',
    customizeColumns: configureVlpGridColumns,
    onInitialized: ensureVlpGridRowNumber,
    onContentReady: ensureVlpGridRowNumber,
};
